import { useTranslation } from 'react-i18next';

import type { FieldSets } from '../types/run-detail.api.types';
import { matrixRow, sendsBasisOf } from '../utils/field-matrix';
import { fieldSetFields } from '../utils/field-plan';

/**
 * The matrix's one-liner — "4 fields to deliver · 5 written · 1 asked for but not written". Shared
 * with the "What ships" card so the card and the matrix it opens never word the same facts apart.
 * `enriched: null` (content-gen didn't answer) reads "— written", never "0 written".
 */
export function useFieldMatrixSummary(data: FieldSets | null | undefined): string {
  const { t } = useTranslation('catalog-enrichment-run');
  if (!data) return '';
  const fields = fieldSetFields(data);
  const deliveredCount = fields.filter((f) => data.delivered.includes(f)).length;
  const enrichedCount = data.enriched?.filter((f) => fields.includes(f)).length ?? 0;
  const attentionCount = fields.filter((f) => matrixRow(data, f).note === 'attention').length;
  // Once integrations confirmed the push the count is the delivered push's list (§90), never a
  // per-field receipt; before that it stays "to deliver".
  const sentKey =
    sendsBasisOf(data) === 'delivered'
      ? 'fieldSets.matrix.summarySentDelivered'
      : 'fieldSets.matrix.summarySent';
  return [
    t(sentKey, { count: deliveredCount }),
    data.enriched === null
      ? t('fieldSets.summaryEnrichedUnknown')
      : t('fieldSets.matrix.summaryWritten', { count: enrichedCount }),
    attentionCount > 0 ? t('fieldSets.matrix.summaryAttention', { count: attentionCount }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * U6 — the muted note shown wherever the field sets' Written facts appear, when content-gen didn't
 * answer (`enriched: null`): "Content generation didn't report what it wrote: <degraded>". Null
 * when `enriched` was reported, so callers render it unconditionally.
 */
export function useFieldSetsNotReportedNote(data: FieldSets | null | undefined): string | null {
  const { t } = useTranslation('catalog-enrichment-run');
  if (data?.enriched !== null) return null;
  const reason = data.degraded?.trim();
  return reason ? t('fieldSets.notReported', { reason }) : t('fieldSets.notReportedNoReason');
}
